import { createContext, useContext, useState, useMemo, useCallback } from "react";
import { useNavigate } from "react-router-dom";

export const PreviaPisCofinsContext = createContext({
  versaoSelecionada: null,
  setVersaoSelecionada: () => {},
  limparVersaoSelecionada: () => {},
  dadosVersao: null,
  setDadosVersao: () => {},
  limparDadosVersao: () => {},
  previaInfo: null,
  setPreviaInfo: () => {},
  limparPreviaInfo: () => {},
  isProjetoIniciado: () => false,
});

export const PreviaPisCofinsProvider = ({ children }) => {
  const [versaoSelecionada, setVersaoSelecionada] = useState(null);
  const [dadosVersao, setDadosVersao] = useState(null);
  const [previaInfo, setPreviaInfo] = useState(null);
  const [reopenModalVersoesConfig, setReopenModalVersoesConfig] = useState(null);

  const navigate = useNavigate();

  const limparVersaoSelecionada = useCallback(() => setVersaoSelecionada(null), []);
  const limparDadosVersao = useCallback(() => setDadosVersao(null), []);
  const limparPreviaInfo = useCallback(() => setPreviaInfo(null), []);

  // Função centralizada para verificar se a prévia está com status PROJETO ou cancelada (congela edição de lançamentos e compor; mesma regra que PROJETO-APROVADO)
  const isProjetoIniciado = useCallback(() => {
    return (
      previaInfo?.status === "PROJETO-INICIADO" ||
      previaInfo?.status === "PROJETO-APROVADO" ||
      previaInfo?.status === "PREVIA-CANCELADA"
    );
  }, [previaInfo]);

  /**
   * Marca a intenção de voltar para a lista de prévias
   * já com a Modal de Versões aberta para a prévia informada.
   *
   * Aceita:
   * - um objeto: { idPreviaPisCofins, dadosEmpresa? }
   * - ou (idPreviaPisCofins, dadosEmpresa?)
   */
  const voltarParaListaComModalVersoes = useCallback(
    (idOrConfig, maybeDadosEmpresa) => {
      const config =
        idOrConfig && typeof idOrConfig === "object"
          ? idOrConfig
          : {
              idPreviaPisCofins: idOrConfig,
              dadosEmpresa: maybeDadosEmpresa,
            };

      // Se não tiver ID da prévia, apenas volta para a lista sem reabrir modal
      if (!config?.idPreviaPisCofins) {
        navigate("/piscofins/previas");
        return;
      }

      setReopenModalVersoesConfig(config);
      navigate("/piscofins/previas");
    },
    [navigate]
  );

  const clearReopenModalVersoesConfig = useCallback(() => {
    setReopenModalVersoesConfig(null);
  }, []);

  const value = useMemo(
    () => ({
      versaoSelecionada,
      setVersaoSelecionada,
      limparVersaoSelecionada,
      dadosVersao,
      setDadosVersao,
      limparDadosVersao,
      previaInfo,
      setPreviaInfo,
      limparPreviaInfo,
      isProjetoIniciado,
      reopenModalVersoesConfig,
      voltarParaListaComModalVersoes,
      clearReopenModalVersoesConfig,
    }),
    [
      versaoSelecionada,
      dadosVersao,
      previaInfo,
      limparVersaoSelecionada,
      limparDadosVersao,
      limparPreviaInfo,
      isProjetoIniciado,
      reopenModalVersoesConfig,
      voltarParaListaComModalVersoes,
      clearReopenModalVersoesConfig,
    ]
  );

  return (
    <PreviaPisCofinsContext.Provider value={value}>
      {children}
    </PreviaPisCofinsContext.Provider>
  );
};

export const usePreviaPisCofins = () => useContext(PreviaPisCofinsContext);
